import { Box, Typography, CircularProgress, Container } from "@mui/material";
import React, { useState, useEffect } from "react";
import { WidgetDto, WorkingApi } from "../../services/netServices";
import ModalDepartment from "./components/ModalDepartment";
import PresentBody from "./components/PresentBody";
import LeaveBody from "./components/LeaveBody";
import NoShowBody from "./components/NoShowBody";
import { Header } from "./components/Header";
import { useStores } from "../../models/helpers/useStores";
import { DepartmentDetail } from "../../services/phpServices";
import { useTranslation } from "react-i18next";

const App: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [workingData, setWorkingData] = useState<WidgetDto[]>([]);
  const [noShowData, setNoShowData] = useState<WidgetDto[]>([]);
  const [leavesData, setLeavesData] = useState<WidgetDto[]>([]);
  const [loadingWorking, setLoadingWorking] = useState<boolean>(true);
  const [loadingNoShow, setLoadingNoShow] = useState<boolean>(true);
  const [loadingLeaves, setLoadingLeaves] = useState<boolean>(true);
  const { t } = useTranslation() as any;
  const {
    appStore: { userProfile, getACLGroup, departments },
  } = useStores();

  const getAllGroupIds = (data: DepartmentDetail[] = []): number[] => {
    return data.flatMap((d) => d.groups?.map((g: any) => g.group_id) || []);
  };

  const groups = getAllGroupIds(departments);

  const filterGroupIdsWithTimesheetApprove = (groupIds: number[]): number[] => {
    if (userProfile?.IsAdmin === 1) {
      return groupIds;
    }
    return groupIds.filter(
      (groupId) => getACLGroup()?.[groupId]?.timesheet?.Approve === true
    );
  };
  const allowedGroups = filterGroupIdsWithTimesheetApprove(groups);

  async function fetchAndSetData(groupIds: number[]) {
    const dataLocalStorage = localStorage.getItem("USER");
    const parsedData = JSON.parse(dataLocalStorage ?? "");
    const token = parsedData?.User?.token;
    try {
      new WorkingApi()
        .workingPresentsPost(token, {
          DepartmentId: 0,
          GroupIds: groupIds,
        })
        .then((presents) => {
          setWorkingData(presents.data);
          setLoadingWorking(false);
        });
      new WorkingApi()
        .workingNoShowsPost(token, {
          DepartmentId: 0,
          GroupIds: groupIds,
        })
        .then((noShows) => {
          setNoShowData(noShows.data);
          setLoadingNoShow(false);
        });
      new WorkingApi()
        .workingLeavesPost(token, {
          DepartmentId: 0,
          GroupIds: groupIds,
        })
        .then((leaves) => {
          setLeavesData(leaves.data);
          setLoadingLeaves(false);
        });
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingWorking(false);
      setLoadingNoShow(false);
      setLoadingLeaves(false);
    }
  }

  useEffect(() => {
    fetchAndSetData(allowedGroups);
  }, []);

  const handleDialogClose = () => setDialogOpen(false);
  const handleDialogOpen = () => setDialogOpen(true);

  const handleSelectGroup = async (ids: number[]) => {
    fetchAndSetData(ids);
  };

  return (
    <>
      <main className="main">
        <Header onPress={handleDialogOpen} />
        <div className="app">
          <Container maxWidth={false} className="h-100">
            <div className="row h-100">
              <section className="col left h-100">
                <Box className="container-box h-100">
                  <Typography variant="h4" mb={2}>
                    {`${t("02_00_present")} (${workingData.length})`}
                  </Typography>
                  <div className="table-container">
                    {loadingWorking && (
                      <div className="loading">
                        {" "}
                        <CircularProgress size={24} />{" "}
                      </div>
                    )}
                    {!loadingWorking && (
                      <>
                        {workingData.length === 0 && (
                          <Typography
                            textAlign="center"
                            className="no-data-container"
                          >
                            {t("20_78_noone_working")}
                          </Typography>
                        )}
                        {workingData.length > 0 && (
                          <PresentBody workingData={workingData} />
                        )}
                      </>
                    )}
                  </div>
                </Box>
              </section>
              <section className="col right h-100">
                <Box className="container-box">
                  <Typography variant="h4" mb={2}>
                    {`${t("02_00_noshow")} (${noShowData.length})`}
                  </Typography>
                  <div className="table-container">
                    {loadingNoShow && (
                      <div className="loading">
                        {" "}
                        <CircularProgress size={24} />{" "}
                      </div>
                    )}
                    {!loadingNoShow && (
                      <>
                        {noShowData.length === 0 && (
                          <Typography
                            textAlign="center"
                            className="no-data-container"
                          >
                            {t("20_78_no_data_noshow")}
                          </Typography>
                        )}
                        {noShowData.length > 0 && (
                          <NoShowBody noShowData={noShowData} />
                        )}
                      </>
                    )}
                  </div>
                </Box>
                <Box className="container-box">
                  <Typography variant="h4" mb={2}>
                    {`${t("02_00_leave")} (${leavesData.length})`}
                  </Typography>
                  <div className="table-container">
                    {loadingLeaves && (
                      <div className="loading">
                        {" "}
                        <CircularProgress size={24} />{" "}
                      </div>
                    )}
                    {!loadingLeaves && (
                      <>
                        {leavesData.length === 0 && (
                          <Typography
                            textAlign="center"
                            className="no-data-container"
                          >
                            {t("20_78_no_data_leave")}
                          </Typography>
                        )}
                        {leavesData.length > 0 && (
                          <LeaveBody leavesData={leavesData} />
                        )}
                      </>
                    )}
                  </div>
                </Box>
              </section>
            </div>
          </Container>
        </div>
      </main>
      <ModalDepartment
        open={dialogOpen}
        onClose={handleDialogClose}
        handleGroupSelect={handleSelectGroup}
      />
    </>
  );
};

export default App;
